import { useState, useEffect, useMemo, useContext } from "react";
import Swal from 'sweetalert2';
import {
    Backdrop,
    Paper,
    Stack,
    Typography,
    InputLabel,
    MenuItem,
    FormControl,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    Skeleton
} from "@mui/material";

import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import EditIcon from '@mui/icons-material/Edit';

import { formatCurrency } from "../utils";
import { UserContext } from "../../context/UserContext";

import CustomButton from "../custom_components/CustomButton";
import CustomIconButton from "../custom_components/CustomIconButton";
import CustomModalHeader from "../custom_components/CustomModalHeader";

import AddPayment from "./AddPayment";
import EditPayment from "./EditPayment";

import { formatToLongDate } from "../../utils/formatToLongDate";

export default function PaymentOrder({
    open,
    onClose,
    onUpdate,
    onUpdateStatus,
    orderInfo
}) {

    const apiUrl = process.env.REACT_APP_API_URL;

    const { paymentMethods } = useContext(UserContext);

    const [selectedPayment, setSelectedPayment] = useState([]);

    const [paymentHist, setPaymentHist] = useState([]);
    const [paymentStat, setPaymentStat] = useState("Pending");

    const [itemAmount, setItemAmount] = useState("");
    const [totalPaidAmount, setTotaPaidAmount] = useState("");

    const [openAddPayment, setOpenAddPayment] = useState(false);
    const [openEditPayment, setOpenEditPayment] = useState(false);

    const [loadingPayments, setLoadingPayments] = useState(false);
    const [loadingPaymentStatus, setLoadingPaymentStatus] = useState(false);
    const [loadingDeletePayment, setLoadingDeletePayment] = useState(null);

    // MEMOIZED TOTAL PAID
    const totalPaid = useMemo(() =>
        paymentHist.reduce(
            (sum, item) =>
                sum + Number(item.PaidAmount || 0),
            0
        ), [paymentHist]
    );

    // GET PAYMENT HISTORY
    const handleGetPaymentHistory = async () => {

        setLoadingPayments(true);
        setPaymentHist([]);

        try {
            const response = await fetch(`${apiUrl}manage_payment/get_payment_history.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    custID: orderInfo.CustomerID,
                    orderID: orderInfo.OrderID,
                })
            });

            const result = await response.json();

            if (result.status === 'success') {

                setPaymentHist(result.payment_history);
                setPaymentStat(result.payment_status.PayStatus || "Pending");

            } else if (result.status === 'error') {
                Swal.fire({ text: result.message, icon: 'info' });
            }

        } catch (error) {
            Swal.fire({ title: 'Server Error!', text: error, icon: 'error' });
            console.error('Server error:', error);
        } finally {
            setLoadingPayments(false);
        }
    }

    // INIATIALIZED
    useEffect(() => {
        const fetchData = async () => {
            if (open) {
                await handleGetPaymentHistory();
                setItemAmount((Number(orderInfo.Price || 0) *
                    Number(orderInfo.ItemQty || 0)) + Number(orderInfo.SFee || 0));
                setPaymentStat(orderInfo?.PaymentStatus || orderInfo?.PayStatus);
            }
        };

        fetchData();
    }, [open]);

    useEffect(() => {
        setTotaPaidAmount(totalPaid);
    }, [totalPaid]);

    // SAVE PAYMENT HISTORY
    const handleSavePaymentHistory = async (info) => {
        setSelectedPayment(info);
        setOpenEditPayment(true);
    };

    // DELETE PAYMENT HISTORY
    const handleDeletePaymentHistory = async (data) => {

        const res = await Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        });

        if (!res.isConfirmed) {
            return;
        }

        setLoadingDeletePayment(data.id);

        try {
            const response = await fetch(`${apiUrl}manage_payment/delete_payment_history.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    id: data.id,
                    custID: data.CustomerID,
                    orderID: data.OrderID,
                }),
            });

            const result = await response.json();

            if (result.status === 'success') {

                const updatedPayments = paymentHist.filter(
                    item => item.id !== data.id
                );

                setPaymentHist(updatedPayments);

                refreshPaymentStatus(updatedPayments);

            } else if (result.status === 'error') {
                Swal.fire({ text: result.message, icon: 'info' });
            }
        } catch (error) {
            Swal.fire({ title: 'Server Error!', text: error, icon: 'error' });
            console.error('Server error:', error);
        } finally {
            setLoadingDeletePayment(null);
        }
    }

    // UPDATE PAYMENT STATUS
    const handleUpdatePaymentStatus = async (value) => {

        setLoadingPaymentStatus(true);

        try {
            const response = await fetch(
                `${apiUrl}manage_payment/update_payment_status.php`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        customerID: orderInfo.CustomerID,
                        orderID: orderInfo.OrderID,
                        payStatus: value
                    }),
                });

            const result = await response.json();

            if (result.status === 'success') {

                await onUpdateStatus({
                    CustomerID: orderInfo.CustomerID,
                    OrderID: orderInfo.OrderID,
                    payStatus: result.message,
                });

                setPaymentStat(value);

            } else if (result.status === 'error') {
                Swal.fire({ text: result.message, icon: 'info' });
            }
        } catch (error) {
            Swal.fire({ title: 'Server Error!', text: error, icon: 'error' });
            console.error('Server error:', error);
        } finally {
            setLoadingPaymentStatus(false);
        }
    };

    // UPDATE PAYMENT STATUS
    const refreshPaymentStatus = (payments) => {
        // Calculate total payments received
        const totalPaidAmount = payments.reduce(
            (total, payment) =>
                total + Number(payment.PaidAmount || 0),
            0
        );

        // Determine payment status
        const isFullyPaid = totalPaidAmount >= Number(itemAmount);
        const isPartiallyPaid =
            totalPaidAmount > 0 &&
            totalPaidAmount < Number(itemAmount);

        let newPaymentStatus = "Pending";

        if (isFullyPaid) {
            newPaymentStatus = "Paid";
        } else if (isPartiallyPaid) {
            newPaymentStatus = "Partially";
        }

        // Update local order data
        onUpdate({
            CustomerID: orderInfo?.CustomerID,
            OrderID: orderInfo?.OrderID,
            PaidAmount: totalPaidAmount,
        });

        // Update payment status only when changed
        if (newPaymentStatus !== paymentStat) {
            handleUpdatePaymentStatus(newPaymentStatus);
        }
    };

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: "blur(1px)",
            }}
        >
            <Paper
                sx={{
                    width: 1000,
                    p: 3,
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,

                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >

                <CustomModalHeader
                    title="Order Payment"
                    subtitle={`ID: ${orderInfo.OrderID}`}
                    onClose={onClose}
                />

                {/* ORDER INFO CARD */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                    sx={{ mt: -2 }}
                >

                    {/* LEFT INFO */}
                    <Stack spacing={0.8}>

                        {/* CUSTOMER */}
                        <Typography
                            sx={{
                                fontSize: 16,
                                fontWeight: 700,
                                color: "#111",
                                lineHeight: 1.2,
                            }}
                        >
                            {orderInfo.CustomerName}
                        </Typography>

                        {/* ITEM + EXTRA INFO */}
                        <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                        >

                            {/* ITEM */}
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    color: "#666",
                                    fontWeight: 500,
                                }}
                            >
                                {orderInfo.ItemName}
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: "#ccc",
                                }}
                            >
                                •
                            </Typography>

                            {/* QTY */}
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: "#888",
                                }}
                            >
                                {orderInfo.ItemQty}x
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: "#ccc",
                                }}
                            >
                                •
                            </Typography>

                            {/* CARGO */}
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: "#888",
                                }}
                            >
                                {orderInfo.Cargo}
                            </Typography>

                        </Stack>

                    </Stack>

                    {/* RIGHT ACTION */}
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                    >

                        <CustomButton
                            label="Add Payment"
                            onClick={() => setOpenAddPayment(true)}
                        />

                    </Stack>

                </Stack>

                {/* TABLE */}
                <TableContainer
                    sx={{
                        maxHeight: 280,
                        border: '1px solid #e0e0e0',
                        borderRadius: 1.2,
                        backgroundColor: '#fff',
                        overflow: 'auto'
                    }}
                >
                    <Table stickyHeader size="small">

                        {/* HEADER */}
                        <TableHead>
                            <TableRow>
                                {[
                                    'Paid Amount',
                                    'Method Used',
                                    'Date',
                                    'Remarks',
                                    'Actions',
                                ].map((header, index) => (
                                    <TableCell key={header} align={header === "Actions" ? "center" : "left"} sx={{
                                        bgcolor: "#1C2536",
                                        color: "#94A3B8",
                                        fontWeight: 700,
                                        fontSize: 11,
                                        letterSpacing: "0.6px",
                                        textTransform: "uppercase",
                                        borderBottom: "none",
                                        py: 1,
                                        width: header === "#" ? 48 : header === "Actions" ? 110 : "auto",
                                    }}>
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        {/* BODY */}
                        <TableBody>

                            {/* LOADING SKELETON */}
                            {loadingPayments && (
                                [...Array(3)].map((_, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Skeleton
                                                variant="text"
                                                width={120}
                                                height={24}
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Skeleton
                                                variant="text"
                                                width={100}
                                                height={24}
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Skeleton
                                                variant="text"
                                                width={160}
                                                height={24}
                                            />
                                        </TableCell>

                                        <TableCell>
                                            <Skeleton
                                                variant="text"
                                                width={200}
                                                height={24}
                                            />
                                        </TableCell>

                                        <TableCell sx={{ display: "flex", direction: "row", gap: 1 }}>
                                            <Skeleton
                                                variant="rounded"
                                                width={24}
                                                height={24}
                                            />
                                            <Skeleton
                                                variant="rounded"
                                                width={24}
                                                height={24}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}

                            {/* DISPLAY DATA */}
                            {!loadingPayments &&
                                paymentHist.map((row, index) => (
                                    <TableRow
                                        key={index}
                                        sx={{
                                            transition: 'all 0.2s ease',

                                            '& td': {
                                                borderBottom:
                                                    index === paymentHist.length - 1
                                                        ? 'none'
                                                        : '1px solid #f1f1f1',
                                            },

                                            '&:hover': {
                                                backgroundColor: '#dcfbff',
                                            },
                                        }}
                                    >
                                        {/* AMOUNT */}
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    color: '#111',
                                                }}
                                            >
                                                {formatCurrency(row.PaidAmount)}
                                            </Typography>
                                        </TableCell>

                                        {/* METHOD */}
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    fontSize: 13.5,
                                                    color: '#555',
                                                }}
                                            >
                                                {row.AccName
                                                    ? `${row.AccType} • ${row.AccName} • ${row.AccNumber}`
                                                    : (
                                                        paymentMethods.find(pm => pm.id === row.PaymentMethod)
                                                            ? `${paymentMethods.find(pm => pm.id === row.PaymentMethod)?.AccType} • ${paymentMethods.find(pm => pm.id === row.PaymentMethod)?.AccName
                                                            } • ${paymentMethods.find(pm => pm.id === row.PaymentMethod)?.AccNumber
                                                            }`
                                                            : row.PaymentMethod
                                                    )
                                                }
                                            </Typography>
                                        </TableCell>

                                        {/* DATE */}
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    color: '#777',
                                                }}
                                            >
                                                {formatToLongDate(row.PaymentDate)}
                                            </Typography>
                                        </TableCell>
                                        {/* DATE */}
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    color: '#777',
                                                }}
                                            >
                                                {row.PaymentRemarks}
                                            </Typography>
                                        </TableCell>

                                        {/* ACTION */}
                                        <TableCell align="center">
                                            <Stack
                                                direction="row"
                                                justifyContent="center"
                                                spacing={1.5}
                                            >
                                                <CustomIconButton
                                                    type="success"
                                                    icon={<EditIcon fontSize="small" />}
                                                    onClick={() => handleSavePaymentHistory(row)}
                                                />

                                                <CustomIconButton
                                                    type="delete"
                                                    loading={loadingDeletePayment === row.id}
                                                    icon={<DeleteIcon fontSize="small" />}
                                                    onClick={() => handleDeletePaymentHistory(row)}
                                                />
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}

                            {/* EMPTY */}
                            {!loadingPayments &&
                                paymentHist.length === 0 && (
                                    <TableRow>
                                        <TableCell
                                            colSpan={4}
                                            align="center"
                                            sx={{
                                                py: 7,
                                                borderBottom: 'none',
                                            }}
                                        >
                                            <Stack
                                                spacing={1}
                                                alignItems="center"
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 15,
                                                        fontWeight: 600,
                                                        color: '#888',
                                                    }}
                                                >
                                                    No payment records
                                                </Typography>

                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        color: '#aaa',
                                                    }}
                                                >
                                                    No payment added yet
                                                </Typography>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                )}
                        </TableBody>

                    </Table>
                </TableContainer>

                {/* SUMMARY */}
                <Stack direction="row" justifyContent="space-between">
                    {/* LEFT */}
                    <Stack
                        sx={{
                            p: 2,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1.5,
                            backgroundColor: '#fafafa',
                            minWidth: 600,
                            gap: 1
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" >
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: '#777',
                                }}
                            >
                                Order Amount (Price x Quantity + SF)
                            </Typography>

                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    color: '#111',
                                }}
                            >
                                {formatCurrency(itemAmount)}
                            </Typography>
                        </Stack>

                        <Divider
                            flexItem
                            sx={{
                                flex: 1,
                                borderColor: "#d0d7de",
                                borderBottomWidth: 1.5,
                            }}
                        />

                        <Stack direction="row" justifyContent="space-between" >
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: '#777',
                                }}
                            >
                                Total Paid
                            </Typography>

                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    color: '#059669',
                                }}
                            >
                                {formatCurrency(totalPaid)}
                            </Typography>
                        </Stack>

                        <Divider
                            flexItem
                            sx={{
                                flex: 1,
                                borderColor: "#d0d7de",
                                borderBottomWidth: 1.5,
                            }}
                        />

                        <Stack direction="row" justifyContent="space-between" >
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    color: '#777',
                                }}
                            >
                                Remaining Balance
                            </Typography>

                            <Typography
                                sx={{
                                    fontWeight: 700,
                                    color: '#d32f2f',
                                }}
                            >
                                {formatCurrency(
                                    itemAmount - totalPaid
                                )}
                            </Typography>
                        </Stack>
                    </Stack>

                    {/* RIGHT */}
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                    >
                        {loadingPaymentStatus && (
                            <CircularProgress size={18} />
                        )}

                        <FormControl
                            aria-hidden="false"
                            size="small"
                            disabled={loadingPaymentStatus}
                            sx={{
                                minWidth: 220,
                                backgroundColor: '#fff',
                            }}
                        >
                            <InputLabel>
                                Payment Status
                            </InputLabel>

                            <Select
                                aria-hidden="false"
                                value={paymentStat}
                                label="Payment Status"
                                onChange={(e) =>
                                    handleUpdatePaymentStatus(
                                        e.target.value
                                    )
                                }
                            >
                                <MenuItem value="Pending">
                                    Pending
                                </MenuItem>
                                <MenuItem value="Partially">
                                    Partially Paid
                                </MenuItem>
                                <MenuItem value="Paid">
                                    Fully Paid
                                </MenuItem>
                                <MenuItem value="Refund">
                                    Refund
                                </MenuItem>
                            </Select>
                        </FormControl>

                    </Stack>

                </Stack>

                {/* POPUP ADD PAYMENTS */}
                <AddPayment
                    open={openAddPayment}
                    onClose={() => setOpenAddPayment(false)}

                    totalPaidAmount={totalPaidAmount}
                    orderInfo={orderInfo}
                    onUpdate={(val) => {
                        const updatedPayments = [
                            ...paymentHist,
                            val
                        ];

                        setPaymentHist(updatedPayments);
                        refreshPaymentStatus(updatedPayments);
                    }}
                />

                {/* POPUP EDIT PAYMENTS */}
                <EditPayment
                    open={openEditPayment}
                    onClose={() => setOpenEditPayment(false)}

                    totalPaidAmount={totalPaidAmount}
                    selectedPayment={selectedPayment}
                    orderInfo={orderInfo}
                    onUpdate={(val) => {
                        const updatedPayments = paymentHist.map(item =>
                            item.id === val.id
                                ? {
                                    ...item,
                                    PaidAmount: val.PaidAmount,
                                    PaymentDate: val.PaymentDate,
                                    PaymentMethod: val.PaymentMethod,
                                    PaymentRemarks: val.PaymentRemarks,
                                }
                                : item
                        );

                        setPaymentHist(updatedPayments);
                        refreshPaymentStatus(updatedPayments);
                    }}
                />

            </Paper>

        </Backdrop>
    );
};